
import { useState } from 'react';
import { useInventory } from './hooks/useInventory';
import { InventoryItem } from './types';
import { generateBarcode } from './lib/barcode';
import { validateWeights, calculateGrossWeight } from '../../mocks/products';

export default function InventoryList() {
  const { useInventoryItems, useCreateInventoryItem } = useInventory();
  const items = useInventoryItems();
  const addItem = useCreateInventoryItem();
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [newItem, setNewItem] = useState<Partial<InventoryItem>>({
    code: '',
    name: '',
    category: '',
    purity: '',
    grossWeight: 0,
    netWeight: 0,
    stoneWeight: 0,
    location: '',
    branch: '',
    status: 'in-stock',
    purchaseDate: new Date().toISOString().split('T')[0],
    value: 0
  });

  const handleWeightChange = (field: 'netWeight' | 'stoneWeight', value: number) => {
    const updatedItem = { ...newItem, [field]: value };
    
    if (field === 'netWeight' || field === 'stoneWeight') {
      const net = field === 'netWeight' ? value : (newItem.netWeight || 0);
      const stone = field === 'stoneWeight' ? value : (newItem.stoneWeight || 0);
      updatedItem.grossWeight = calculateGrossWeight(net, stone);
    }
    
    setNewItem(updatedItem);
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate weights
    const gross = newItem.grossWeight || 0;
    const net = newItem.netWeight || 0;
    const stone = newItem.stoneWeight || 0;
    
    if (gross <= 0) {
      alert('Gross weight must be greater than zero');
      return;
    }
    
    if (!validateWeights(gross, net, stone)) {
      alert('Weight calculation error: Gross weight must equal net weight + stone weight');
      return;
    }
    
    try {
      const itemData = {
        ...newItem,
        code: newItem.code || generateBarcode('PJ'),
        grossWeight: gross,
        netWeight: net,
        stoneWeight: stone
      };
      await addItem.mutateAsync(itemData);
      setShowAddForm(false);
      setNewItem({
        code: '',
        name: '',
        category: '',
        purity: '',
        grossWeight: 0,
        netWeight: 0,
        stoneWeight: 0,
        location: '',
        branch: '',
        status: 'in-stock',
        purchaseDate: new Date().toISOString().split('T')[0],
        value: 0
      });
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  // Safe filtering with proper null checks
  const filteredItems = (items.data || []).filter((item: InventoryItem) => {
    const matchesSearch = (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.code || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (items.isLoading) return <div className="p-6">Loading inventory...</div>;
  if (items.error) return <div className="p-6 text-red-600">Error loading inventory: {items.error.message}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-primary">Inventory Management</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line mr-2"></i>
          Add Item
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <i className="ri-box-3-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Items</p>
              <p className="text-2xl font-bold text-gray-900">{(items.data || []).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <i className="ri-check-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">In Stock</p>
              <p className="text-2xl font-bold text-gray-900">
                {(items.data || []).filter(item => item.status === 'in-stock').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <i className="ri-time-line text-yellow-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Reserved</p>
              <p className="text-2xl font-bold text-gray-900">
                {(items.data || []).filter(item => item.status === 'reserved').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <i className="ri-money-rupee-circle-line text-purple-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{(items.data || []).reduce((sum, item) => sum + (item.value || 0), 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 pr-8"
        >
          <option value="all">All Status</option>
          <option value="in-stock">In Stock</option>
          <option value="sold">Sold</option>
          <option value="reserved">Reserved</option>
          <option value="qc">QC</option>
        </select>
        <div className="text-sm text-gray-600">
          Showing {filteredItems.length} of {(items.data || []).length} items
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-box-3-line text-6xl text-gray-400 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No inventory items found</h3>
            <p className="text-gray-600 mb-4">
              {(items.data || []).length === 0 
                ? "Start by adding your first inventory item" 
                : "Try adjusting your search or filter criteria"
              }
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line mr-2"></i>
              Add First Item
            </button>
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-accent/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-primary">Code</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Name</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Category</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Purity</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Net Wt (g)</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Stone Wt (g)</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Gross Wt (g)</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Branch</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Status</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Value</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item: InventoryItem) => (
                <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{item.code || 'N/A'}</td>
                  <td className="px-4 py-3">{item.name || 'N/A'}</td>
                  <td className="px-4 py-3">{item.category || 'N/A'}</td>
                  <td className="px-4 py-3">{item.purity || 'N/A'}</td>
                  <td className="px-4 py-3">{(item.netWeight || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">{(item.stoneWeight || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 font-medium">{(item.grossWeight || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">{item.branch || 'N/A'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'in-stock' ? 'bg-green-100 text-green-800' :
                      item.status === 'sold' ? 'bg-gray-100 text-gray-800' :
                      item.status === 'reserved' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {item.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">₹{(item.value || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <button className="text-primary hover:text-primary/80 mr-2 cursor-pointer">
                      <i className="ri-edit-line"></i>
                    </button>
                    <button className="text-gray-600 hover:text-gray-800 cursor-pointer">
                      <i className="ri-qr-code-line"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary">Add New Item</h2>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleAddItem} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Code</label>
                  <input
                    type="text"
                    value={newItem.code}
                    onChange={(e) => setNewItem({ ...newItem, code: e.target.value })}
                    placeholder="Auto-generated if empty"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                  <input
                    type="text"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 pr-8"
                  >
                    <option value="">Select Category</option>
                    <option value="Ring">Ring</option>
                    <option value="Necklace">Necklace</option>
                    <option value="Earrings">Earrings</option>
                    <option value="Bracelet">Bracelet</option>
                    <option value="Pendant">Pendant</option>
                    <option value="Chain">Chain</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purity</label>
                  <select
                    value={newItem.purity}
                    onChange={(e) => setNewItem({ ...newItem, purity: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 pr-8"
                  >
                    <option value="">Select Purity</option>
                    <option value="22K">22K</option>
                    <option value="18K">18K</option>
                    <option value="14K">14K</option>
                    <option value="916">916</option>
                    <option value="750">750</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Net Weight (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newItem.netWeight}
                    onChange={(e) => handleWeightChange('netWeight', parseFloat(e.target.value) || 0)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stone Weight (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newItem.stoneWeight}
                    onChange={(e) => handleWeightChange('stoneWeight', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gross Weight (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newItem.grossWeight}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={newItem.location}
                    onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                    placeholder="Shelf/Counter location"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                  <select
                    value={newItem.branch}
                    onChange={(e) => setNewItem({ ...newItem, branch: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 pr-8"
                  >
                    <option value="">Select Branch</option>
                    <option value="Main Store">Main Store</option>
                    <option value="Mall Branch">Mall Branch</option>
                    <option value="City Center">City Center</option>
                    <option value="Warehouse">Warehouse</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value (₹)</label>
                <input
                  type="number"
                  value={newItem.value}
                  onChange={(e) => setNewItem({ ...newItem, value: parseFloat(e.target.value) || 0 })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={addItem.isPending}
                  className="flex-1 bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {addItem.isPending ? 'Adding...' : 'Add Item'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
